import React, { useState } from 'react';
import clsx from 'clsx';
import { TableData } from './types';
import { formatValue, parseValue } from './formatter';

type EditableTableProps = {
  data: TableData;
  editedCells: Map<string, string>;
  onCellChange: (rowIndex: number, colIndex: number, value: string) => void;
  tablePrefix: string;
  deferFormattingWhileEditing?: boolean;
  compact?: boolean;
};

export const EditableTable = ({
  data,
  editedCells,
  onCellChange,
  tablePrefix,
  deferFormattingWhileEditing = false,
  compact = false,
}: EditableTableProps) => {
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const columns = data?.config?.columns ?? [];
  const rows = data?.rows ?? [];
  const lockedRows = data?.config?.lockedRows ?? [];
  const isEdited = (rowIndex: number, colIndex: number) => {
    return editedCells.has(`${tablePrefix}-${rowIndex}-${colIndex}`);
  };

  const isLocked = (rowIndex: number, colIndex: number) => {
    return (
      lockedRows.includes(rowIndex) ||
      columns[colIndex]?.locked === true
    );
  };

  const handleChange = (rowIndex: number, colIndex: number, value: string) => {
    const format = columns[colIndex]?.format;
    const parsedValue = parseValue(value, format);
    onCellChange(rowIndex, colIndex, parsedValue);
  };

  return (
    <div className="overflow-x-auto">
      <table className={clsx(
        "divide-y divide-gray-200",
        compact ? "w-full min-w-[1100px] table-fixed" : "min-w-full"
      )}>
        {compact ? (
          <colgroup>
            {columns.map((_, index) => (
              <col key={index} className={index === 0 ? "w-[92px]" : undefined} />
            ))}
          </colgroup>
        ) : null}
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className={clsx(
                  "font-medium text-gray-500 uppercase whitespace-nowrap",
                  compact
                    ? column.locked
                      ? "px-1 py-2 text-center text-xs font-semibold tracking-normal"
                      : "px-1 py-2 text-center text-[10px] tracking-normal"
                    : "px-6 py-3 text-left text-xs tracking-wider"
                )}
              >
                {column.label}
                {column.locked && (
                  <span className="ml-1 text-gray-400">(Bloqueado)</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row, rowIndex) => (
            <tr 
              key={rowIndex} 
              className={clsx(
                "hover:bg-gray-50 align-content-center",
                lockedRows.includes(rowIndex) && "bg-gray-50"
              )}
            >
              {row.map((cell, colIndex) => {
                const format = columns[colIndex]?.format;
                const isLockedColumn = columns[colIndex]?.locked === true;
                const locked = isLocked(rowIndex, colIndex);
                const edited = isEdited(rowIndex, colIndex);
                const cellKey = `${tablePrefix}-${rowIndex}-${colIndex}`;
                const rawValue = edited ? editedCells.get(cellKey)! : cell;
                const displayValue = deferFormattingWhileEditing && focusedCell === cellKey
                  ? String(rawValue ?? '')
                  : formatValue(rawValue, format);

                return (
                  <td
                    key={`${rowIndex}-${colIndex}`}
                    className={clsx(
                      "whitespace-nowrap",
                      compact ? "px-1 py-3" : "px-6 py-4",
                      locked ? "bg-gray-50" : "bg-white",
                      compact && isLockedColumn && "text-center",
                      format === 'money' && "text-right",
                      format === 'number' && "text-right"
                    )}
                  >
                    {locked ? (
                      <span className={clsx(
                        "text-gray-900",
                        compact
                          ? isLockedColumn ? "text-sm font-medium" : "text-xs"
                          : "text-sm"
                      )}>{displayValue}</span>
                    ) : (
                      <input
                        type="text"
                        value={displayValue}
                        size={compact ? 1 : undefined}
                        title={compact ? displayValue : undefined}
                        onFocus={() => setFocusedCell(cellKey)}
                        onBlur={() => setFocusedCell((current) => current === cellKey ? null : current)}
                        onChange={(e) => handleChange(rowIndex, colIndex, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === ',' || e.key === 'Decimal' || e.keyCode === 188) {
                            e.preventDefault();
                          }
                        }}
                        className={clsx(
                          "w-full rounded border focus:ring-2 focus:ring-blue-500 transition-colors",
                          compact
                            ? "min-w-0 px-0.5 py-1 text-[clamp(10px,0.7vw,12px)] tabular-nums"
                            : "px-2 py-1",
                          edited ? "bg-blue-50 border-blue-300" : "border-transparent hover:border-gray-300",
                          format === 'money' && "text-right",
                          format === 'number' && "text-right"
                        )}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};